import { Fragment, useEffect, useRef, useState } from "react";
import { Transition } from "@headlessui/react";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import type {
    FundDataWithType,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import { Translate } from "../../../hooks/useTranslation";
import Button from "../../Button";
import ShareIcon from "../../icons/Share";
import Spinner from "../../Spinner";

interface Props {
    fund: FundDataWithType;
    shareClasses: ShareClassDataWithType[];
    show: "watch" | "subscribe" | "none";
    onClose: () => void;
    isSubscribed?: boolean;
}

export const FundCardOverlay = ({
    fund,
    shareClasses,
    show,
    onClose,
    isSubscribed,
}: Props) => {
    const { buttonClickEvent } = useYardActionEvents();
    const basket = useFCSubscriptionStore((state) => state.selectionBasket);
    const toggleBasketItem = useFCSubscriptionStore(
        (state) => state.toggleBasketItem,
    );
    const setBasketMode = useFCSubscriptionStore(
        (state) => state.setBasketMode,
    );
    const setSharingMode = useFCSubscriptionStore(
        (state) => state.setSharingMode,
    );
    const watchFunds = useFCSubscriptionStore((state) => state.watchFunds);
    const resetFlow = useFCSubscriptionStore((state) => state.resetFlow);
    const user = useFCSubscriptionStore((state) => state.user);
    const ref = useRef<HTMLDivElement>(null);
    const [isActioning, setIsActioning] = useState(false);
    const triggerAnalyticsEvent = (
        type: "subscribe" | "share" | "watch" | "multi",
    ) => {
        buttonClickEvent({
            name:
                type === "watch"
                    ? "Watch"
                    : type === "share"
                      ? "Share"
                      : type === "multi"
                        ? "Select multiple funds"
                        : "Subscribe",
            fund: {
                id: fund.data.id,
                name: fund.data.data.name,
                type: fund.type,
            },
            position: "Subscription pop up",
            details: fund.data.data.name,
        });
    };
    const handleSingleAction = async (
        evt: React.MouseEvent<HTMLAnchorElement>,
    ) => {
        if (show === "none") return;
        setIsActioning(true);
        resetFlow();
        triggerAnalyticsEvent(show);
        if (user && show === "watch") {
            evt.preventDefault();
            await watchFunds([fund]);
        } else {
            setBasketMode(show);
            toggleBasketItem(fund, shareClasses);
        }
        setIsActioning(false);
        onClose();
    };
    const handleMultiAction = () => {
        if (show === "none") return;
        setSharingMode("none");
        setBasketMode(show);
        toggleBasketItem(fund, shareClasses);
        triggerAnalyticsEvent("multi");
    };
    const handleShare = () => {
        if (show !== "none") {
            resetFlow();
            setBasketMode(show);
            setSharingMode(show);
            toggleBasketItem(fund, shareClasses);
            triggerAnalyticsEvent("share");
        }
    };

    useEffect(() => {
        if (show) {
            const closeOutside = (evt: MouseEvent) => {
                if (!ref.current?.contains(evt.target as Node)) {
                    onClose();
                }
            };
            document.addEventListener("mousedown", closeOutside);
            return () =>
                document.removeEventListener("mousedown", closeOutside);
        }
    }, [onClose, show]);

    useEffect(() => {
        if (basket.funds.length > 0 && show !== "none") {
            onClose();
        }
    }, [basket.funds.length, onClose, show]);

    let subscriptionUrl = "/my-funds";

    switch (show) {
        case "watch":
            subscriptionUrl += "/watch";
            break;
        case "subscribe":
            subscriptionUrl += "/subscribe";
            break;
    }

    return (
        <Transition
            show={show !== "none"}
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div
                className="bg-charcoal/30 absolute inset-0 z-40 grid cursor-default place-items-center overflow-visible max-sm:p-2"
                onClick={(evt) => {
                    evt.stopPropagation();
                    onClose();
                }}
                ref={ref}
            >
                <div
                    className="flex w-full flex-col gap-2 rounded bg-white p-2 shadow-lg max-sm:h-full max-sm:max-h-[115px] sm:max-w-[18rem]"
                    onClick={(evt) => {
                        evt.stopPropagation();
                    }}
                >
                    <div
                        className={cx(
                            "flex gap-4",
                            isSubscribed && "max-sm:my-auto",
                        )}
                    >
                        <Button
                            color="white"
                            className="flex-[1] py-3 font-normal sm:py-1.5"
                            as="link"
                            to={subscriptionUrl + "/share"}
                            onClick={handleShare}
                            stripSearchParams={["step"]}
                            icon={<ShareIcon className="h-4 w-4 text-white" />}
                        >
                            <Translate>Share</Translate>
                        </Button>
                        {isSubscribed ? (
                            <Button
                                color="primary"
                                className="flex-[2] font-normal"
                                as="link"
                                to="/my-funds/manage"
                                onClick={() => {
                                    const tabs =
                                        document.querySelectorAll<HTMLAnchorElement>(
                                            "a.fund-centre-tab:not(#my-funds)",
                                        );
                                    tabs.forEach((tab) => {
                                        tab.classList.remove("selected");
                                    });
                                }}
                            >
                                <Translate>Manage your funds</Translate>
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                className="relative flex-[1] py-3 font-normal sm:flex-[2] sm:py-1.5"
                                as="link"
                                to={subscriptionUrl}
                                onClick={handleSingleAction}
                                stripSearchParams={["step"]}
                            >
                                {isActioning && (
                                    <Spinner className="absolute left-3 h-4 w-4 border-2 border-x-white" />
                                )}
                                {show === "none" ? null : show === "watch" ? (
                                    <Translate>Watch</Translate>
                                ) : (
                                    <Translate>Subscribe</Translate>
                                )}
                            </Button>
                        )}
                    </div>
                    {!isSubscribed && (
                        <div className="flex justify-center max-sm:my-auto sm:justify-end">
                            <button
                                className="text-charcoal text-sm font-medium underline sm:text-xs sm:font-normal"
                                onClick={handleMultiAction}
                            >
                                <Translate>Select multiple funds</Translate>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Transition>
    );
};
